/**
 * Slim top navigation bar with the official FTI wordmark.
 */

import React from 'react';
import { Button, Flex, useTheme, spacing, fontFamily } from '@fti/design-system';
import { useAuth, useUser, useProviderLogout } from '../auth/hooks';
import { getConfig } from '../config';
import { FtiLogo } from './FtiLogo';

export const AppHeader: React.FC = () => {
  const { theme } = useTheme();
  const auth = useAuth();
  const user = useUser();
  const config = getConfig();
  const { signoutRedirect } = useProviderLogout();

  const isProd = config.name === 'prod';
  const envColor = isProd
    ? theme.colors.semantic.error.default
    : theme.colors.primary[600];
  const envBg = isProd
    ? `${theme.colors.semantic.error.light}30`
    : theme.colors.primary[50];

  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        width: '100%',
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'saturate(180%) blur(12px)',
        WebkitBackdropFilter: 'saturate(180%) blur(12px)',
        borderBottom: `1px solid ${theme.colors.border.default}`,
        fontFamily: fontFamily.sans,
      }}
    >
      <Flex
        align="center"
        justify="between"
        style={{
          maxWidth: '1180px',
          margin: '0 auto',
          padding: `${spacing.md} ${spacing.xl}`,
        }}
      >
        <Flex align="center" gap="md">
          <FtiLogo height={20} color={theme.colors.primary[700]} />
          <span
            aria-hidden
            style={{
              width: 1,
              height: 22,
              background: theme.colors.border.default,
            }}
          />
          <span
            style={{
              fontSize: '0.95rem',
              fontWeight: 600,
              color: theme.colors.text.primary,
              letterSpacing: '-0.01em',
            }}
          >
            Quote Extractor
          </span>
          <span
            style={{
              fontSize: '0.65rem',
              fontWeight: 700,
              color: envColor,
              background: envBg,
              padding: '3px 9px',
              borderRadius: '999px',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {config.displayName ?? config.name}
          </span>
        </Flex>

        <Flex align="center" gap="md">
          {auth.isAuthenticated && user?.email && (
            <span
              style={{
                fontSize: '0.85rem',
                color: theme.colors.text.secondary,
              }}
            >
              {user.email}
            </span>
          )}
          {auth.isAuthenticated && (
            <Button
              variant="outline"
              color="primary"
              size="sm"
              onClick={() => signoutRedirect()}
            >
              Sign out
            </Button>
          )}
          {!auth.isAuthenticated && !auth.isLoading && (
            <Button
              variant="solid"
              color="primary"
              size="sm"
              onClick={() => auth.signinRedirect()}
            >
              Sign in
            </Button>
          )}
        </Flex>
      </Flex>
    </header>
  );
};
